import { z } from 'zod';
import { appIdSchema } from './app.js';
import { billingDimensionSchema, planCodeSchema, planIntervalSchema } from './entitlement.js';
import { expectedPriceFields, minorUnitsSchema, prorationDateSchema } from './expected-price.js';
export { MAX_PREVIEW_AMOUNT_MINOR_UNITS, MAX_PRORATION_DATE_SECONDS, expectedPriceFields, expectedPriceSchema, prorationDateSchema, } from './expected-price.js';
/**
 * PRICE-CHANGE PREVIEWS — handoff 0010 §5, the whole-product rule: any action that changes what
 * a tenant is charged, now or later, shows a preview first and proceeds only on explicit
 * confirmation carrying the amounts the user saw.
 *
 * Three pieces live here, and every money-changing route consumes all three:
 *
 * 1. `priceChangeSchema` — WHAT is being previewed. A discriminated union on `kind`. It never
 *    carries a quantity: a `quantity` change names a DELTA (the intent — "one more location"),
 *    and the server derives the resulting quantity from its own count (docs/019, handoff §3).
 * 2. `pricePreviewDTOSchema` — the answer. All amounts are integers in MINOR UNITS of
 *    `currency`, the same convention `Price.amountMinorUnits` uses, so no float touches money.
 * 3. `expectedPriceFields` — the stale-preview guard. A confirming request spreads these into
 *    its own body schema; the server recomputes the preview and refuses with 409
 *    `PRICE_CHANGED` (details: `priceChangedDetailsSchema`, carrying the FRESH preview) when
 *    either amount differs. A customer is never charged an amount they did not see.
 */
/** The largest single quantity step one change may preview (a bulk import included). */
export const MAX_PREVIEW_QUANTITY_DELTA = 10_000;
/** The dimensions a tenant action can move. `FLAT` never changes by action and `METERED` is
 *  counted by the provider, so neither is previewable as a quantity change. */
export const PREVIEWABLE_QUANTITY_DIMENSIONS = ['LOCATION', 'SEAT'];
/**
 * One change to what a tenant pays. `.strict()` on every member: an extra field (a smuggled
 * `quantity`, say) is a client bug, never silently ignored input.
 *
 * - `plan` — move an application to `planCode` at `interval`. With no provider subscription this
 *   is a first checkout; otherwise an upgrade (charged now, prorated) or a downgrade / annual →
 *   monthly (at period end) — the server classifies, the client never says which.
 * - `free` — the whole tenant down to the free tier (every billed line ends at period end).
 * - `quantity` — `delta` more (positive) or fewer (negative) units of `dimension`: creating,
 *   deleting, activating or deactivating a location; inviting or deactivating a member. The
 *   server derives the resulting quantity from its own live count; a client quantity is never
 *   accepted.
 * - `add_app` / `remove_app` — an application's line added to or removed from the subscription.
 * - `cancel_change` — withdraw `app`'s pending period-end plan change (handoff 0010 §2 "Cancel
 *   change"). Money-changing: the tenant keeps paying its CURRENT price at the renewal instead
 *   of the scheduled one, so the preview's `currentRecurring` is what the pending change would
 *   have billed and `newRecurring` what the tenant will pay instead, from `effectiveAt`.
 */
export const priceChangeSchema = z.discriminatedUnion('kind', [
    z
        .object({
        kind: z.literal('plan'),
        planCode: planCodeSchema,
        interval: planIntervalSchema,
    })
        .strict(),
    z.object({ kind: z.literal('free') }).strict(),
    z
        .object({
        kind: z.literal('quantity'),
        dimension: z.enum(PREVIEWABLE_QUANTITY_DIMENSIONS),
        delta: z
            .number()
            .int()
            .min(-MAX_PREVIEW_QUANTITY_DELTA)
            .max(MAX_PREVIEW_QUANTITY_DELTA)
            .refine((delta) => delta !== 0, { message: 'delta must not be zero' }),
    })
        .strict(),
    z.object({ kind: z.literal('add_app'), planCode: planCodeSchema }).strict(),
    z.object({ kind: z.literal('remove_app'), app: appIdSchema }).strict(),
    z.object({ kind: z.literal('cancel_change'), app: appIdSchema }).strict(),
    z.object({ kind: z.literal('resume_app'), app: appIdSchema }).strict(),
]);
/**
 * When a previewed change takes effect — handoff §2/§3 timing, decided by the SERVER:
 *
 * - `immediate` — now. `dueNow` is what is charged today (an upgrade, a quantity increase).
 * - `period_end` — at `effectiveAt`, the end of the current billing period. Nothing is charged
 *   or credited now (a downgrade, annual → monthly, down to Free, a quantity decrease).
 * - `after_trial` — the tenant is in a free period; nothing is due now and `newRecurring` bills
 *   from `effectiveAt` (= `trialEndsAt`): "free during trial, $Z/month after <date>". Also the
 *   answer for a paid plan chosen DURING the signup trial (`priceChangeOfCheckout`): hosted
 *   checkout collects the card, the trial keeps its end, and the first charge is at `trialEndsAt`.
 * - `at_checkout` — no provider subscription yet; `dueNow` is charged when hosted checkout
 *   completes (the first period, in advance). A tenant with no trial, or with under 48 hours of
 *   its trial left — too near for the provider's checkout to carry.
 */
export const PRICE_CHANGE_TIMINGS = ['immediate', 'period_end', 'after_trial', 'at_checkout'];
export const priceChangeTimingSchema = z.enum(PRICE_CHANGE_TIMINGS);
/** What happens to one billed line. `unchanged` lines are included so a caller can show the
 *  whole resulting bill; the shared dialog lists only the others. */
export const PRICE_PREVIEW_LINE_CHANGES = ['added', 'removed', 'changed', 'unchanged'];
/** One side (before or after) of one billed line. `amount` = `unitAmount × quantity`, computed
 *  on the server so a client never does money arithmetic. */
export const pricePreviewLineStateSchema = z
    .object({
    planCode: planCodeSchema,
    interval: planIntervalSchema,
    unitAmount: minorUnitsSchema,
    quantity: z.number().int().min(0).max(1_000_000),
    amount: minorUnitsSchema,
})
    .strict();
export const pricePreviewLineDTOSchema = z
    .object({
    app: appIdSchema,
    dimension: billingDimensionSchema,
    change: z.enum(PRICE_PREVIEW_LINE_CHANGES),
    /** NULL for an `added` line. */
    before: pricePreviewLineStateSchema.nullable(),
    /** NULL for a `removed` line. */
    after: pricePreviewLineStateSchema.nullable(),
})
    .strict();
/**
 * How one headline amount breaks down (handoff 0012 §1), all MINOR UNITS of the preview's
 * `currency`, as the billing provider's invoice states them: `subtotal` before discounts and
 * exclusive tax, `discount` (coupons, free periods), `tax`, and `total` — the amount charged.
 * `subtotal - discount + tax = total` for exclusive tax; a tax-inclusive price already holds its
 * tax in `subtotal`, so there the identity is `subtotal - discount = total`.
 */
export const pricePreviewAmountsSchema = z
    .object({
    subtotal: minorUnitsSchema,
    discount: minorUnitsSchema,
    tax: minorUnitsSchema,
    total: minorUnitsSchema,
})
    .strict();
/**
 * Every `kind` a preview answers: the tenant-side `PriceChange` kinds plus the two OPERATOR kinds
 * (`OperatorPriceChange`, handoff 0010 §5 row 10) — one DTO, so the one shared dialog shows both.
 */
export const PRICE_PREVIEW_KINDS = [
    'plan',
    'free',
    'quantity',
    'add_app',
    'remove_app',
    'cancel_change',
    'resume_app',
    'grant_trial',
    'assign_plan',
];
/**
 * The answer to every preview. Amounts are MINOR UNITS of `currency`.
 *
 * - `dueNow` — charged today (0 unless `timing` is `immediate` or `at_checkout`).
 * - `currentRecurring` / `newRecurring` — the recurring total at `interval` before and after
 *   the change takes effect, summed over the lines billed at that interval. A tenant whose lines
 *   bill at mixed intervals (ADR-0028) sees the other interval's lines in `lines` only.
 * - `interval` — NULL only when nothing is billed at all (e.g. a free tenant).
 * - `currentInterval` — the interval `currentRecurring` is billed at (handoff 0014 §1): the
 *   before side of the same line `interval` reads the after side of, so a monthly → annual change
 *   reads "Currently $129 / month" and "$1,290 / year". NULL when nothing is billed today.
 * - `effectiveAt` — ISO-8601; now for `immediate`/`at_checkout`, the period end or trial end
 *   otherwise.
 * - `trialEndsAt` — set only for `after_trial`.
 * - `prorationDate` — handoff 0014 §2: the instant (EPOCH SECONDS) this preview was priced at.
 *   The confirming request echoes it (`expectedPriceFields`), and the server re-prices and the
 *   provider charges at exactly this instant — so the amount charged is the amount shown.
 * - `unpaidForgiven` — handoff 0013 §1: the applications whose UNPAID amount the change cancels
 *   (down to Free while a renewal is unpaid: the tenant is never charged for a period it chose to
 *   leave). Absent when nothing is forgiven.
 * - `dueNowAmounts` / `newRecurringAmounts` — handoff 0012 §1: how `dueNow` / `newRecurring` break
 *   down. Each `total` IS the headline figure (`dueNow` / `newRecurring` keep meaning the total).
 * - `isEstimate` — handoff 0012 §1b: the amounts are OUR arithmetic over the catalog, not the
 *   billing provider's invoice (the fake adapter: local dev and every test). Excludes tax.
 * - `taxIncluded` — tax is in the totals. False when it cannot be known yet (no subscription,
 *   so no billing address: a first checkout adds "applicable tax" on the provider's own page)
 *   and for an estimate.
 */
export const pricePreviewDTOSchema = z
    .object({
    kind: z.enum(PRICE_PREVIEW_KINDS),
    timing: priceChangeTimingSchema,
    effectiveAt: z.string().datetime(),
    trialEndsAt: z.string().datetime().nullable(),
    prorationDate: prorationDateSchema,
    currency: z.string().regex(/^[A-Z]{3}$/),
    interval: planIntervalSchema.nullable(),
    currentInterval: planIntervalSchema.nullable(),
    dueNow: minorUnitsSchema,
    currentRecurring: minorUnitsSchema,
    newRecurring: minorUnitsSchema,
    dueNowAmounts: pricePreviewAmountsSchema,
    newRecurringAmounts: pricePreviewAmountsSchema,
    isEstimate: z.boolean(),
    taxIncluded: z.boolean(),
    lines: z.array(pricePreviewLineDTOSchema).max(50),
    unpaidForgiven: z.array(appIdSchema).max(10).optional(),
})
    .strict();
/** `details` of a 409 `PRICE_CHANGED`: the freshly computed preview, so the UI re-shows the new
 *  numbers without a second round trip. */
export const priceChangedDetailsSchema = z.object({ preview: pricePreviewDTOSchema });
export function parsePriceChangedDetails(details) {
    const parsed = priceChangedDetailsSchema.safeParse(details);
    return parsed.success ? parsed.data : null;
}
/**
 * `POST /api/web/billing/subscription/change` — handoff 0010 §1–§2: a paying tenant changes its
 * Visitor plan in the app (never the hosted portal). Discriminated on `kind`, mirroring the
 * `plan` / `free` members of `priceChangeSchema` — the body IS the previewed change plus the
 * amounts the user saw (`expectedPriceFields`), so the route previews exactly what it confirms.
 *
 * - `plan` — another self-serve plan and/or interval. An upgrade or monthly → annual takes effect
 *   now and is charged now (prorated); a downgrade or annual → monthly at the end of the period.
 * - `free` — end the paid Visitor plan at the end of the period and fall back to Visitor Free.
 */
export const planChangeRequestSchema = z.discriminatedUnion('kind', [
    z
        .object({
        kind: z.literal('plan'),
        planCode: planCodeSchema,
        interval: planIntervalSchema,
        ...expectedPriceFields,
    })
        .strict(),
    z.object({ kind: z.literal('free'), ...expectedPriceFields }).strict(),
]);
/** The `PriceChange` a plan-change request previews and confirms — the one mapping both the
 *  server and a client use, so the two can never preview different changes. */
export function priceChangeOfPlanChange(request) {
    return request.kind === 'plan'
        ? { kind: 'plan', planCode: request.planCode, interval: request.interval }
        : { kind: 'free' };
}
/**
 * `POST /api/web/billing/subscription/free` — choosing the free tier (S26-TASK-016). The body is
 * optional and carries nothing; a tenant with more SUBSCRIBED locations than the free tier allows
 * is refused with 409 `SUBSCRIBED_LOCATIONS_OVER_LIMIT` (handoff 0011).
 */
export const freePlanRequestSchema = z.object({}).strict();
/* -------------------------------------------------------------------------- */
/* Handoff 0010 Phase D1 — the remaining money-changing routes                 */
/* -------------------------------------------------------------------------- */
/**
 * The `PriceChange` a checkout confirms (`POST /billing/checkout-session`, §5 row 1): the plan
 * at the requested interval (monthly when omitted — the checkout's own default). One mapping
 * for server and client.
 */
export function priceChangeOfCheckout(request) {
    return { kind: 'plan', planCode: request.planCode, interval: request.interval ?? 'MONTH' };
}
/**
 * `DELETE /api/web/billing/apps/:app` (§5 row 4) — the body is only the amounts the owner was
 * shown for `{ kind: 'remove_app', app }`.
 */
/** Why an owner cancelled an application — the standard answers offered in the cancel dialog. */
export const CANCEL_REASON_CODES = [
    'too_expensive',
    'not_using',
    'missing_features',
    'switching',
    'temporary',
    'other',
];
/** The longest free-text note a cancelling owner may leave. */
export const CANCEL_COMMENT_MAX_LENGTH = 500;
export const removeAppRequestSchema = z
    .object({
    ...expectedPriceFields,
    /** Optional: the owner may skip the question. Recorded on the audit row, never required. */
    reason: z.enum(CANCEL_REASON_CODES).optional(),
    /** Optional free text, trimmed; an empty note is treated as none. */
    comment: z
        .string()
        .trim()
        .max(CANCEL_COMMENT_MAX_LENGTH)
        .transform((value) => (value === '' ? undefined : value))
        .optional(),
})
    .strict();
/**
 * `DELETE /api/web/billing/subscription/change` — cancelling a pending plan change moves what
 * the tenant pays at the renewal back to today's price, so it is previewed and confirmed like
 * any other change: the body is the amounts shown for `{ kind: 'cancel_change', app }`.
 */
export const cancelPlanChangeRequestSchema = z.object({ ...expectedPriceFields }).strict();
/**
 * `POST /api/web/billing/apps/:app/resume` — un-cancelling an application that is set to end at
 * its period end puts the renewal charge back, so it is previewed and confirmed like any other
 * change: the body is the amounts shown for `{ kind: 'resume_app', app }`.
 */
export const resumeAppRequestSchema = z.object({ ...expectedPriceFields }).strict();
/**
 * The SEAT preview a member write confirms (§5 row 6). An invite is `+1` — the seat is billed
 * when the invitee ACCEPTS, but the admin's invite is the decision point (the invitee is not the
 * payer), so the admin is shown "when they accept, Directory rises …" and the accept itself does
 * not re-confirm. Reactivating is `+1` too (an accepted member returns ACTIVE; a never-accepted
 * one gets their invite back, the same decision as inviting); deactivating an ACTIVE member `-1`.
 * `seats` > 1 is a batch — one aggregate preview, never N silent increments (§5 row 7).
 */
export function priceChangeOfSeats(delta) {
    return { kind: 'quantity', dimension: 'SEAT', delta };
}
/**
 * `POST /api/web/members/price-preview` — the same
 * preview as `POST /billing/preview`, narrowed to the one dimension the module's own permission
 * governs, so a `tenant.admin` (who manages members but not billing) sees the numbers before
 * confirming a member write (handoff §5: "by the customer, by one of their admins …").
 */
export const memberPricePreviewRequestSchema = z
    .object({
    kind: z.literal('quantity'),
    dimension: z.literal('SEAT'),
    delta: z
        .number()
        .int()
        .min(-MAX_PREVIEW_QUANTITY_DELTA)
        .max(MAX_PREVIEW_QUANTITY_DELTA)
        .refine((d) => d !== 0, { message: 'delta must not be zero' }),
})
    .strict();
/** True when a preview changes nothing the tenant pays — nothing due now and the same
 *  recurring amount. A client MAY skip the confirmation dialog for such a preview and confirm
 *  with its (zero) amounts directly; the server still compares them. */
export function isZeroPriceChange(preview) {
    return preview.dueNow === 0 && preview.currentRecurring === preview.newRecurring;
}
/** The amounts a preview asks the confirming request to carry — and the instant it was priced
 *  at (handoff 0014 §2), so the confirm re-prices at that same instant. */
export function expectedPriceOf(preview) {
    return {
        expectedDueNow: preview.dueNow,
        expectedRecurring: preview.newRecurring,
        prorationDate: preview.prorationDate,
    };
}
/* -------------------------------------------------------------------------- */
/* Handoff 0010 Phase D2 — operator price changes, the next invoice            */
/* -------------------------------------------------------------------------- */
/**
 * A platform operator's change to ONE tenant's plan (handoff 0010 §5 row 10) — previewed by
 * `POST /api/web/billing/tenants/:tenantId/price-preview` (`platform.entitlement.manage`) and
 * confirmed by the operator route it names, whose body carries `expectedPriceFields`:
 *
 * - `grant_trial` — `PUT /billing/tenants/:tenantId/apps/:app/trial`. Provider-less: nothing is
 *   charged now or automatically later; the preview states what the plan costs at the trial's
 *   end (`after_trial`) if the tenant then chooses it.
 * - `assign_plan` — `PUT /billing/tenants/:tenantId/apps/:app/plan`. A sales-led (or unlaunched)
 *   plan, provider-less and non-expiring: nothing is billed through the provider.
 *
 * Per-tenant entitlement OVERRIDES are not here on purpose: an override moves an entitlement,
 * never a price, a billed quantity or a line (docs/019 rule 6 — the billed quantity is the live
 * count, not the limit), so it changes nothing the tenant pays (docs/019 rule 8, exemptions).
 */
export const operatorPriceChangeSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('grant_trial'), app: appIdSchema, planCode: planCodeSchema }).strict(),
    z.object({ kind: z.literal('assign_plan'), app: appIdSchema, planCode: planCodeSchema }).strict(),
]);
/**
 * The next invoice the provider will raise for the tenant's subscription, with no change
 * (handoff 0010 §5 row 9 — renewals, and row 8's "billing starts <date> at $Y" for a
 * provider-backed trial). `GET /api/web/billing/upcoming-invoice` answers `{ data: null }` when
 * there is none: no provider subscription (a free tenant, a provider-less trial), or one that
 * ends before it renews.
 *
 * - `amountDue` — minor units of `currency`, what the invoice will collect (after any credit).
 * - `date` — ISO-8601, when the provider will raise it (the renewal or the trial's end).
 * - `interval` — the interval the subscription's lines bill at; NULL when they are mixed.
 * - `trialEndsAt` — set when the invoice is the FIRST after a provider-backed trial: billing
 *   starts then.
 */
export const nextInvoiceDTOSchema = z
    .object({
    amountDue: minorUnitsSchema,
    currency: z.string().regex(/^[A-Z]{3}$/),
    date: z.string().datetime(),
    interval: planIntervalSchema.nullable(),
    trialEndsAt: z.string().datetime().nullable(),
})
    .strict();
//# sourceMappingURL=price-preview.js.map